import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DialogContent from "@mui/material/DialogContent";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import DownloadIcon from "@mui/icons-material/Download";
import { usePatrolsStore } from "./patrolsStore/patrolsStore";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { AlertsTableSelectedPatrol } from "./AlertsTableSelectedPatrol";
import { CHMWarningsTableSelectedPatrol } from "./CHMWarningsTableSelectedPatrol";
import { useNavigate } from "react-router-dom";
import { downloadPatrolReport } from "./patrolsUtils/downloadPatrolReport";

const theme = createTheme({
  palette: {
    primary: {
      main: "#FF8400",
    },
  },
});

export const PatrolDialog = () => {
  const navigate = useNavigate();
  const {
    patrolDialogOpen,
    setPatrolDialogOpen,
    selectedPatrol,
    patrolsData,
    selectedPatrolIndex,
    setSelectedPatrolIndex,
    setSelectedPatrol,
    fetchPatrolDialogData,
    patrolDialogDataLoading,
    patrolAlertVideoUrl,
    patrolAlertVideoLoading,
    selectedPatrolAlertIndex,
    patrolCHMImageUrl,
    patrolCHMImageLoading,
    selectedPatrolCHMIndex,
    setSelectedPatrolAlertIndex,
    setSelectedPatrolCHMIndex,
    patrolAlerts,
    patrolCHMIssues,
    fetchPatrolAlertVideo,
    fetchPatrolCHMImage,
    patrolSummary,
    searchQueryPatrols,
  } = usePatrolsStore();

  const [selectedTab, setSelectedTab] = React.useState("one");
  const [downloadingReport, setDownloadingReport] = React.useState(false);

  // Handle download patrol report
  const handleDownloadPatrolReport = async () => {
    if (!selectedPatrol?.patrol_id) return;
    setDownloadingReport(true);
    try {
      await downloadPatrolReport(String(selectedPatrol.patrol_id));
    } catch (error) {
      console.error("Error downloading patrol report:", error);
      alert("Unable to download patrol report");
    } finally {
      setDownloadingReport(false);
    }
  };

  // Fetch alerts and CHM warnings when selectedPatrol changes
  React.useEffect(() => {
    if (selectedPatrol?.patrol_id && patrolDialogOpen) {
      fetchPatrolDialogData(selectedPatrol.patrol_id, navigate);
    }
  }, [
    selectedPatrol?.patrol_id,
    patrolDialogOpen,
    fetchPatrolDialogData,
    navigate,
  ]);

  const filteredPatrols = React.useMemo(
    () =>
      patrolsData.filter(
        (patrol) =>
          patrol.site
            ?.toLowerCase()
            .includes(searchQueryPatrols.toLowerCase()) ||
          patrol.patrol_date
            ?.toLowerCase()
            .includes(searchQueryPatrols.toLowerCase()) ||
          patrol.start_time
            ?.toLowerCase()
            .includes(searchQueryPatrols.toLowerCase()) ||
          patrol.schedule_name
            ?.toLowerCase()
            .includes(searchQueryPatrols.toLowerCase())
      ),
    [patrolsData, searchQueryPatrols]
  );

  const hasPatrols = filteredPatrols.length > 0;
  const clampedPatrolIndex = hasPatrols
    ? Math.min(Math.max(selectedPatrolIndex, 0), filteredPatrols.length - 1)
    : 0;

  const canPrevAlert = patrolAlerts.length > 0 && selectedPatrolAlertIndex > 0;
  const canNextAlert =
    patrolAlerts.length > 0 &&
    selectedPatrolAlertIndex < patrolAlerts.length - 1;
  const canPrevCHM = patrolCHMIssues.length > 0 && selectedPatrolCHMIndex > 0;
  const canNextCHM =
    patrolCHMIssues.length > 0 &&
    selectedPatrolCHMIndex < patrolCHMIssues.length - 1;

  const handlePrevMedia = () => {
    if (selectedTab === "one") {
      if (!canPrevAlert) return;
      const newIndex = selectedPatrolAlertIndex - 1;
      setSelectedPatrolAlertIndex(newIndex);
      const windowId = patrolAlerts[newIndex]?.window_id;
      if (windowId) fetchPatrolAlertVideo(windowId, navigate);
    } else {
      if (!canPrevCHM) return;
      const newIndex = selectedPatrolCHMIndex - 1;
      setSelectedPatrolCHMIndex(newIndex);
      const imageKey = patrolCHMIssues[newIndex]?.image_s3_key;
      fetchPatrolCHMImage(imageKey, navigate);
    }
  };

  const handleNextMedia = () => {
    if (selectedTab === "one") {
      if (!canNextAlert) return;
      const newIndex = selectedPatrolAlertIndex + 1;
      setSelectedPatrolAlertIndex(newIndex);
      const windowId = patrolAlerts[newIndex]?.window_id;
      if (windowId) fetchPatrolAlertVideo(windowId, navigate);
    } else {
      if (!canNextCHM) return;
      const newIndex = selectedPatrolCHMIndex + 1;
      setSelectedPatrolCHMIndex(newIndex);
      const imageKey = patrolCHMIssues[newIndex]?.image_s3_key;
      fetchPatrolCHMImage(imageKey, navigate);
    }
  };

  return (
    <div>
      <Dialog open={patrolDialogOpen} fullWidth={true} maxWidth={"xl"}>
        <DialogTitle sx={{ marginLeft: 2, p: 2, position: "relative" }}>
          <div className="flex justify-between items-center">
            <div className="flex items-center flex-1 pr-4">
              <p className="font-mulish text-actuate-blue">
                {selectedPatrol
                  ? `${selectedPatrol.site} | ${selectedPatrol.patrol_date} ${selectedPatrol.start_time} Patrol`
                  : "Patrol Details"}
              </p>
              <OrangeTextButton
                onClick={handleDownloadPatrolReport}
                text={
                  downloadingReport ? (
                    <CircularProgress size={20} sx={{ color: "#FF8400" }} />
                  ) : (
                    <Tooltip title="Download Patrol" placement="top">
                      <DownloadIcon />
                    </Tooltip>
                  )
                }
                disabled={downloadingReport || !selectedPatrol?.patrol_id}
              />
            </div>
            <div className="flex items-center text-gray-600 text-[14px]">
              <span className="mr-4">
                {hasPatrols ? clampedPatrolIndex + 1 : 0} of{" "}
                {filteredPatrols.length}
              </span>
              <OrangeTextButton
                onClick={() => {
                  const nextIndex = clampedPatrolIndex - 1;
                  setSelectedPatrolIndex(nextIndex);
                  setSelectedPatrol(filteredPatrols[nextIndex]);
                }}
                text={
                  <Tooltip title="Previous Patrol" placement="top">
                    <ArrowBackIosIcon fontSize="small" id="fowardButton" />
                  </Tooltip>
                }
                disabled={!hasPatrols || clampedPatrolIndex === 0}
              />
              <OrangeTextButton
                onClick={() => {
                  const nextIndex = clampedPatrolIndex + 1;
                  setSelectedPatrolIndex(nextIndex);
                  setSelectedPatrol(filteredPatrols[nextIndex]);
                }}
                text={
                  <Tooltip title="Next Patrol" placement="top">
                    <ArrowForwardIosIcon fontSize="small" id="backButton" />
                  </Tooltip>
                }
                disabled={
                  !hasPatrols ||
                  clampedPatrolIndex === filteredPatrols.length - 1
                }
              />
              <IconButton
                aria-label="close"
                onClick={() => {
                  setPatrolDialogOpen(false);
                }}
                sx={{
                  color: (theme) => theme.palette.grey[500],
                  ml: 1,
                }}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>
        </DialogTitle>
        <DialogContent dividers sx={{ height: 550 }}>
          {patrolDialogDataLoading ? (
            <div className="flex justify-center items-center h-full">
              <CircularProgress sx={{ color: "#FF8400" }} />
            </div>
          ) : (
            <div className="flex flex-col w-full">
              <div
                className="flex flex-col w-full pb-4 mb-4 font-mulish border-b border-gray-200"
                style={{ fontFamily: "Mulish, sans-serif" }}
              >
                <p className="font-mulish font-bold text-actuate-blue mb-1">
                  Patrol Summary
                </p>
                <p
                  className="font-mulish text-gray-700"
                  style={{ fontSize: "0.875rem" }}
                >
                  {patrolSummary || "No summary available."}
                </p>
              </div>
              <div className="flex gap-2 justify-center items-start min-w-0">
                <div className="flex flex-col flex-1 min-w-0 max-w-[800px]">
                  <ThemeProvider theme={theme}>
                    <Tabs
                      value={selectedTab}
                      onChange={(event, tabValue) => {
                        setSelectedTab(tabValue);
                      }}
                      aria-label="patrol tabs"
                      color="primary"
                      variant="fullWidth"
                      sx={{ width: "100%", maxWidth: 800, marginBottom: "16px" }}
                    >
                      <Tab
                        value="one"
                        label={`Alerts (${patrolAlerts.length})`}
                        sx={{
                          fontFamily: "Mulish",
                          fontWeight: "Bold",
                          color: "primary",
                        }}
                      />
                      <Tab
                        value="two"
                        label={`Connectivity (${patrolCHMIssues.length})`}
                        sx={{
                          fontFamily: "Mulish",
                          fontWeight: "Bold",
                          color: "primary",
                        }}
                      />
                    </Tabs>
                  </ThemeProvider>
                  {selectedTab === "one" && <AlertsTableSelectedPatrol />}
                  {selectedTab === "two" && <CHMWarningsTableSelectedPatrol />}
                </div>
                <div className="flex items-center justify-center gap-2 pl-[25px] pt-[25px]">
                  <OrangeTextButton
                    onClick={handlePrevMedia}
                    text={
                      <Tooltip
                        title={
                          selectedTab === "one"
                            ? "Previous Alert"
                            : "Previous CHM Warning"
                        }
                        placement="top"
                      >
                        <ArrowBackIosIcon fontSize="small" />
                      </Tooltip>
                    }
                    disabled={
                      selectedTab === "one" ? !canPrevAlert : !canPrevCHM
                    }
                  />
                  <div
                    className="flex flex-col items-center justify-center"
                    style={{ width: 500, height: 300 }}
                  >
                    {selectedTab === "one" ? (
                      patrolAlertVideoLoading ? (
                        <CircularProgress sx={{ color: "#FF8400" }} />
                      ) : patrolAlertVideoUrl ? (
                        <video
                          key={`${selectedPatrolIndex}-${selectedPatrolAlertIndex}`}
                          width="500"
                          controls
                          autoPlay
                          loop
                        >
                          <source src={patrolAlertVideoUrl} type="video/mp4" />
                          <track kind="captions" src="" />
                        </video>
                      ) : (
                        <p className="text-gray-500 font-mulish">
                          No video available
                        </p>
                      )
                    ) : patrolCHMImageLoading ? (
                      <CircularProgress sx={{ color: "#FF8400" }} />
                    ) : patrolCHMImageUrl ? (
                      <img
                        key={`${selectedPatrolIndex}-${selectedPatrolCHMIndex}`}
                        src={patrolCHMImageUrl}
                        alt="CHM Warning"
                        style={{
                          maxWidth: 500,
                          maxHeight: 300,
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <p className="text-gray-500 font-mulish">
                        No image available
                      </p>
                    )}
                  </div>
                  <OrangeTextButton
                    onClick={handleNextMedia}
                    text={
                      <Tooltip
                        title={
                          selectedTab === "one"
                            ? "Next Alert"
                            : "Next CHM Warning"
                        }
                        placement="top"
                      >
                        <ArrowForwardIosIcon fontSize="small" />
                      </Tooltip>
                    }
                    disabled={
                      selectedTab === "one" ? !canNextAlert : !canNextCHM
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
